import React from 'react';
import { FileText, Plus, Camera, Search, Trash2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/**
 * Document Items Table
 * Reusable items table for documents
 */
const DocumentItemsTable = ({
  items = [],
  onUpdateItem,
  onDeleteItem,
  onAddItem,
  onScanBarcode,
  onSearchItem,
  searchTerm = '',
  searchResults = [],
  showSearchDropdown = false,
  onSelectSearchResult,
  errors = {}
}) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  return (
    <Card className="p-4 lg:p-6 border-2 transition-all shadow-lg hover:shadow-xl" style={{
      borderColor: '#dbeafe',
      background: 'linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)'
    }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
            <div className="p-2 rounded-lg" style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)' }}>
              <FileText className="w-4 h-4 text-blue-600" />
            </div>
            Items
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">{items.length} item{items.length !== 1 ? 's' : ''} added</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Quick add item..."
              value={searchTerm}
              onChange={(e) => onSearchItem(e.target.value)}
              className="pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64 transition-all"
            />
            {showSearchDropdown && searchResults.length > 0 && (
              <div className="absolute z-20 w-96 right-0 mt-2 bg-white border-2 border-blue-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                {searchResults.map(item => (
                  <div
                    key={item.id}
                    onClick={() => onSelectSearchResult(item)}
                    className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-sm text-gray-900">{item.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-600">Code: {item.code || 'N/A'}</span>
                          {item.hsn && <span className="text-xs text-gray-600">• HSN: {item.hsn}</span>}
                        </div>
                      </div>
                      <div className="text-right ml-3">
                        <p className="font-bold text-blue-600">{formatCurrency(item.selling_price || item.sales_price || 0)}</p>
                        <p className="text-xs text-gray-500">{item.primary_unit || item.unit || 'PCS'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {onScanBarcode && (
            <Button 
              size="sm" 
              variant="outline" 
              onClick={onScanBarcode}
              className="whitespace-nowrap border-2 transition-all"
              style={{ borderColor: '#bbf7d0', backgroundColor: 'white' }}
              title="Scan barcode or QR code"
            >
              <Camera className="w-4 h-4 mr-1" />
              Scan
            </Button>
          )}
          <Button 
            size="sm" 
            variant="outline" 
            onClick={onAddItem} 
            className="whitespace-nowrap border-2 transition-all"
            style={{ borderColor: '#bfdbfe', backgroundColor: 'white' }}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Line
          </Button>
        </div>
      </div>

      {/* Table with horizontal scroll on mobile */}
      <div className="overflow-x-auto -mx-6 px-6">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-gray-50 border-y">
            <tr>
              <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600">#</th>
              <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600 min-w-[200px]">ITEM</th>
              <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600">HSN/SAC</th>
              <th className="px-2 py-2 text-right text-xs font-semibold text-gray-600">QTY</th>
              <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600">UNIT</th>
              <th className="px-2 py-2 text-right text-xs font-semibold text-gray-600">UNIT PRICE</th>
              <th className="px-2 py-2 text-right text-xs font-semibold text-gray-600">DISCOUNT</th>
              <th className="px-2 py-2 text-right text-xs font-semibold text-gray-600">TAX RATE</th>
              <th className="px-2 py-2 text-right text-xs font-semibold text-gray-600">AMOUNT</th>
              <th className="px-2 py-2 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan="10" className="py-8 text-center text-gray-500">
                  <p className="text-sm">No items added yet</p>
                  <p className="text-xs mt-1">Click "Add Line" or search for items to get started</p>
                </td>
              </tr>
            ) : (
              items.map((item, index) => (
                <tr key={index} className="border-b hover:bg-gray-50">
                  <td className="px-2 py-2 text-gray-600">{item.line_no || index + 1}</td>
                  <td className="px-2 py-2">
                    <input
                      type="text"
                      value={item.name || ''}
                      onChange={(e) => onUpdateItem(index, 'name', e.target.value)}
                      placeholder="Item name"
                      className={`w-full px-2 py-1 border rounded focus:ring-1 focus:ring-blue-500 ${errors[`item_${index}_name`] ? 'border-red-500' : 'border-gray-300'}`}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="text"
                      value={item.hsn || ''}
                      onChange={(e) => onUpdateItem(index, 'hsn', e.target.value)}
                      placeholder="HSN"
                      className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="number"
                      value={item.quantity || ''}
                      onChange={(e) => onUpdateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-right focus:ring-1 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="text"
                      value={item.unit || 'PCS'}
                      onChange={(e) => onUpdateItem(index, 'unit', e.target.value)}
                      className="w-16 px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="number"
                      value={item.unit_price || ''}
                      onChange={(e) => onUpdateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                      className="w-24 px-2 py-1 border border-gray-300 rounded text-right focus:ring-1 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="number"
                      value={item.discount || ''}
                      onChange={(e) => onUpdateItem(index, 'discount', parseFloat(e.target.value) || 0)}
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-right focus:ring-1 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <select
                      value={item.tax_rate || 0}
                      onChange={(e) => onUpdateItem(index, 'tax_rate', parseFloat(e.target.value))}
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-right focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="0">0%</option>
                      <option value="5">5%</option>
                      <option value="12">12%</option>
                      <option value="18">18%</option>
                      <option value="28">28%</option>
                    </select>
                  </td>
                  <td className="px-2 py-2 text-right font-semibold text-gray-900">
                    {formatCurrency(item.amount || 0)}
                  </td>
                  <td className="px-2 py-2">
                    <button
                      onClick={() => onDeleteItem(index)}
                      className="p-1 hover:bg-red-100 rounded transition-colors"
                      title="Delete item"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default DocumentItemsTable;